import type { IncomingMessage } from 'http';

import { BREAKPOINT_SIZES } from '@/utils/constants/ui';
import { isSearchEngineBot } from '@/utils/httpUtil';
import getViewportWidthFromDocumentCookie from '@/utils/ui/getViewportWidthFromDocumentCookie';
import parseCssPx from '@/utils/ui/parseCssPx';

import { getSsrViewportWidthFromReq, resolveCanonicalViewportWidth } from './getSsrViewportWidth';

const BOT_SSR_VIEWPORT_WIDTH = parseCssPx(BREAKPOINT_SIZES.DESKTOP_MIN) ?? 1440;

type ResolveSsrViewportWidthOptions = {
    /**
     * When true and no cookie/hint is available on SSR, return `undefined` for regular users
     * so {@link AppProvider} ViewportRevealShell stays active until client `matchMedia` resolves.
     * Bots still receive a stable desktop width so crawlers are not served hidden content.
     */
    revealWhenUnknown?: boolean;
};

/**
 * Viewport width for `_app` getInitialProps: prefers `tn_vw` / Client Hints from `req`,
 * then falls back to `document.cookie` when `getInitialProps` runs in the browser (no `req`).
 *
 * By default, when `req` is present but neither source yields a width, returns a canonical
 * desktop fallback so AppProvider does not wrap SSR HTML in `opacity: 0`. Pass
 * `{ revealWhenUnknown: true }` to opt into ViewportRevealShell for real users instead.
 */
const resolveSsrViewportWidth = (
    req: IncomingMessage | undefined,
    options: ResolveSsrViewportWidthOptions = {},
): number | undefined => {
    const { revealWhenUnknown = false } = options;
    const fromReq = getSsrViewportWidthFromReq(req);
    if (fromReq != null) {
        return fromReq;
    }
    if (req == null) {
        return getViewportWidthFromDocumentCookie();
    }
    if (revealWhenUnknown) {
        return isSearchEngineBot(req) ? BOT_SSR_VIEWPORT_WIDTH : undefined;
    }
    return resolveCanonicalViewportWidth(undefined);
};

export { BOT_SSR_VIEWPORT_WIDTH, resolveSsrViewportWidth };
export type { ResolveSsrViewportWidthOptions };
